
import React, { useState, useEffect } from 'react';
import LeftColumn from './LeftColumn';
import RightColumn from './RightColumn';
import ContactPickerModal from './ContactPickerModal';
import API_BASE_URL from '../../../../Constants';
import { TEMPLATES } from './EmailTemplate';

export default function InspectionEmailNotification() {
  // ---------- E-MAIL STATES ----------
  const [fromRecipient, setFromRecipient] = useState(null);
  const [toRecipients, setToRecipients] = useState([]);
  const [ccRecipients, setCcRecipients] = useState([]);
  const [bccRecipients, setBccRecipients] = useState([]);
  const [showBcc, setShowBcc] = useState(false);


  const [attachment, setAttachment] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  const [emailSender, setEmailSender] = useState('someone@example.com');
  const [contactToUse, setContactToUse] = useState('Main Contact');
  const [emailedCount, setEmailedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);

  const [selectedTemplate, setSelectedTemplate] = useState('');

  // bridgingMap => { [inspId]: [arrayOfDeviceObjects] }
  const [bridgingMap, setBridgingMap] = useState({});

  // Contact Picker
  const [showContactModal, setShowContactModal] = useState(false);
  const [recipientType, setRecipientType] = useState('to');

  // Layout toggles
  const [leftHasInsp, setLeftHasInsp] = useState(false);
  const leftWidth = leftHasInsp ? 'w-1/2' : 'w-1/4';
  const rightWidth = leftHasInsp ? 'w-1/2' : 'w-3/4';

  // “CAN SEND?” => require at least a “To” + Subject
  const canSend = toRecipients.length > 0 && emailSubject.trim() !== '';

  // (A) Template selection
  function handleTemplateChange(newTemplateKey) {
    setSelectedTemplate(newTemplateKey);
    if (TEMPLATES[newTemplateKey]) {
      setEmailSubject(TEMPLATES[newTemplateKey].subject);
      // We'll not forcibly overwrite the body if you prefer
      // setEmailBody(TEMPLATES[newTemplateKey].body);
    } else {
      setEmailSubject('');
      setEmailBody('');
    }
    setEmailBody(buildEmailBody(newTemplateKey, bridgingMap));
  }

  // (B) Clear
  function handleClearEmail() {
    setEmailSender('');
    setEmailSubject('');
    setEmailBody('');
    setAttachment('');
    setSelectedFile(null);

    setFromRecipient(null);
    setToRecipients([]);
    setCcRecipients([]);
    setBccRecipients([]);
    setShowBcc(false);
    setSelectedTemplate('');
    setBridgingMap({});
  }

  // (C) Send => calls “/api/email-tickets/sendAndCreate” on the back end
  async function handleSendEmail() {
    if (!canSend) {
      alert('Need at least a “To” and a Subject!');
      return;
    }

    // If emailBody is empty, fallback
    let bodyToSend = emailBody;
    if (!bodyToSend || !bodyToSend.trim()) {
      bodyToSend = 'No body was provided.';
    }

    // Build a payload with the *new* property names the server expects
    const payload = {
      // The back end expects "emailFrom", not "fromEmail"
      emailFrom: fromRecipient?.email || emailSender,

      // The back end expects "emailSubject", "emailBody"
      emailSubject,
      emailBody: bodyToSend,

      // For attachments
      attachment: attachment || '',

      // Instead of 'to', 'cc', 'bcc', we do 'toRecipients', 'ccRecipients', 'bccRecipients'
      toRecipients: toRecipients.map(r => r.email),
      ccRecipients: ccRecipients.map(r => r.email),
      bccRecipients: bccRecipients.map(r => r.email),

      contactType: contactToUse,
      emailedCount,
      rejectedCount,
      templateUsed: selectedTemplate || '(none)',

      // If you want bridging info stored, pass it too:
      bridgingMap
    };

    try {
      const resp = await fetch(`${API_BASE_URL}/api/email-tickets/sendAndCreate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        throw new Error(`sendAndCreate => ${resp.status}`);
      }

      const result = await resp.json();
      console.log('sendAndCreate =>', result);

      setEmailedCount(prev => prev + 1);

      alert(`E-Mail sent and ticket created! (Ticket #${result.ticketId || '??'})`);
    } catch (err) {
      console.error(err);
      alert('Failed to send e-mail or create ticket.');
    }
  }

  // (D) Placeholder
  function handleOpenNewWindow() {
    alert('Open new window => placeholder');
  }

  // (E) Contact Picker => sets which line we pick
  function openPicker(type) {
    setRecipientType(type);
    setShowContactModal(true);
  }

  // (F) after picking a contact
  function handleAddRecipient(contact) {
    if (recipientType === 'from') {
      setFromRecipient(contact);
    } else if (recipientType === 'to') {
      setToRecipients(prev => [...prev, contact]);
    } else if (recipientType === 'cc') {
      setCcRecipients(prev => [...prev, contact]);
    } else {
      setBccRecipients(prev => [...prev, contact]);
    }
  }

  // (G) render recipients => chips
  function renderRecipients(list) {
    return list.map(r => (
      <span key={r.email} className="bg-gray-100 px-2 py-1 rounded text-xs mr-1 mb-1 inline-block">
        {r.name || r.email}
      </span>
    ));
  }

  // (H) bridging data => store & rebuild body
  function handleBridgingData(inspId, devices) {
    setBridgingMap(prev => {
      const newMap = { ...prev, [inspId]: devices };
      return newMap;
    });
    setEmailBody(buildEmailBody(selectedTemplate, {
      ...bridgingMap,
      [inspId]: devices,
    }));
  }

  // (I) remove from bridgingMap => rebuild
  function handleRemoveInspectionFromMap(inspId) {
    setBridgingMap(prev => {
      const copy = { ...prev };
      delete copy[inspId];
      return copy;
    });
    const copy = { ...bridgingMap };
    delete copy[inspId];
    setEmailBody(buildEmailBody(selectedTemplate, copy));
  }

  // (J) build final e-mail body => base template + "cards"
  function buildEmailBody(templateKey, mapOfDevices) {
    if (!templateKey || !TEMPLATES[templateKey]) {
      return buildInspectionCards(mapOfDevices, '');
    }
    const baseBody = TEMPLATES[templateKey].body;
    return buildInspectionCards(mapOfDevices, baseBody);
  }

  // (K) build "cards" for each inspection => device details
  function buildInspectionCards(mapOfDevices, baseText) {
    if (!mapOfDevices || Object.keys(mapOfDevices).length === 0) {
      return baseText;
    }

    let finalStr = baseText.trim();
    finalStr += '\n\n----- INSPECTION DETAILS -----\n';

    Object.entries(mapOfDevices).forEach(([inspId, devices]) => {
      finalStr += `\n+----------------------------------------+\n`;
      finalStr += `|  INSPECTION #${inspId}\n`;
      finalStr += `+----------------------------------------+\n`;

      if (!devices || devices.length === 0) {
        finalStr += `  (No devices selected)\n`;
      } else {
        devices.forEach(dev => {
          finalStr += `  DEVICE #${dev.deviceId}\n`;
          finalStr += `    Step: ${dev.status || 'N/A'}\n`;
          finalStr += `    Violations: ${dev.violationCount ?? 0}\n`;
          finalStr += `    Inspector: ${dev.inspectedBy || 'N/A'}\n`;
          finalStr += `    Date: ${dev.inspectionDate || 'N/A'}\n`;
          finalStr += `  ----------------------------------------\n`;
        });
      }
    });

    return finalStr;
  }

  return (
    <div className="bg-gray-100 p-4">
      <h1 className="text-2xl font-bold mb-4">Inspection E-Mailer</h1>

      <div className="flex items-stretch min-h-screen gap-4">
        {/* LEFT COLUMN => bridging + template selection */}
        <div className={`${leftWidth} bg-white shadow-sm flex flex-col`}>
          <LeftColumn
            onHasInspections={bool => setLeftHasInsp(bool)}
            selectedTemplate={selectedTemplate}
            onTemplateChange={handleTemplateChange}
            handleBridgingData={handleBridgingData}
            onRemoveInspectionFromMap={handleRemoveInspectionFromMap}
          />
        </div>

        {/* RIGHT COLUMN => e-mail form */}
        <div className={`${rightWidth} bg-white shadow-sm flex flex-col`}>
          <RightColumn
            fromRecipient={fromRecipient}
            setFromRecipient={setFromRecipient}
            toRecipients={toRecipients}
            ccRecipients={ccRecipients}
            bccRecipients={bccRecipients}
            showBcc={showBcc}
            setShowBcc={setShowBcc}
            emailSubject={emailSubject}
            setEmailSubject={setEmailSubject}
            emailBody={emailBody}
            setEmailBody={setEmailBody}
            attachment={attachment}
            setAttachment={setAttachment}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            contactToUse={contactToUse}
            setContactToUse={setContactToUse}
            emailedCount={emailedCount}
            rejectedCount={rejectedCount}
            handleOpenNewWindow={handleOpenNewWindow}
            handleClearEmail={handleClearEmail}
            handleSendEmail={handleSendEmail}
            canSend={canSend}
            emailSender={emailSender}
            setEmailSender={setEmailSender}
            openPicker={openPicker}
            renderRecipients={renderRecipients}
          />
        </div>
      </div>

      {/* CONTACT PICKER MODAL */}
      <ContactPickerModal
        open={showContactModal}
        onClose={() => setShowContactModal(false)}
        onAddRecipient={contact => {
          handleAddRecipient(contact);
          setShowContactModal(false);
        }}
        recipientType={recipientType}
      />
    </div>
  );
}